import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';
import { formatCalendarDate } from '../../common/calendarDate';
import { fetchStripePayouts, fetchSyncedPayouts, StripePayout, SyncedStripePayout, triggerMipGiftSync } from '../../services/finance-services';
import CreateMockPayoutModal from './CreateMockPayoutModal';

type DataSource = 'stripe' | 'synced';

const statusColors: Record<string, string> = {
    paid: 'bg-success text-white',
    pending: 'bg-warning text-black',
    in_transit: 'bg-primary text-white',
    failed: 'bg-danger text-white',
    canceled: 'bg-meta-1 text-white',
};

const mipStatusColors: Record<string, string> = {
    synced: 'bg-success text-white',
    pending: 'bg-warning text-black',
    processing: 'bg-primary text-white',
    failed: 'bg-danger text-white',
    'n/a': 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300',
    'not_processed': 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300',
};

const Payouts = () => {
    const [loading, setLoading] = useState(true);
    const [payouts, setPayouts] = useState<(StripePayout | SyncedStripePayout)[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [loadingMore, setLoadingMore] = useState(false);
    const [syncingPayoutId, setSyncingPayoutId] = useState<string | null>(null);
    const [syncError, setSyncError] = useState<{ payoutId: string; message: string } | null>(null);
    const [syncSuccess, setSyncSuccess] = useState<{ payoutId: string; recordCount: number } | null>(null);
    const [dataSource, setDataSource] = useState<DataSource>('synced');
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [showMockPayoutModal, setShowMockPayoutModal] = useState(false);

    const loadPayouts = async (cursor?: string, append = false) => {
        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            setError(null);

            if (dataSource === 'stripe') {
                // Fetch from Stripe API
                const params: { limit?: number; starting_after?: string; status?: string } = {
                    limit: 25,
                };
                if (cursor) params.starting_after = cursor;
                if (statusFilter) params.status = statusFilter;

                const response = await fetchStripePayouts(params);

                if (append) {
                    setPayouts((prev) => [...prev, ...response.payouts]);
                } else {
                    setPayouts(response.payouts);
                }
                setHasMore(response.has_more);
                setNextCursor(response.next_cursor);
                setTotalCount(null); // Stripe doesn't provide total count
            } else {
                // Fetch from local synced records
                const offset = cursor ? parseInt(cursor, 10) : 0;
                const params: { limit?: number; offset?: number; status?: string } = {
                    limit: 25,
                    offset: append ? offset : 0,
                };
                if (statusFilter) params.status = statusFilter;

                const response = await fetchSyncedPayouts(params);

                if (append) {
                    setPayouts((prev) => [...prev, ...response.payouts]);
                } else {
                    setPayouts(response.payouts);
                }
                setHasMore(response.has_more);
                setNextCursor(response.next_cursor);
                setTotalCount(response.total_count);
            }
        } catch (e) {
            console.error('Error fetching payouts:', e);
            setError(e instanceof Error ? e.message : 'Failed to fetch payouts');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        loadPayouts();
    }, [statusFilter, dataSource]);

    const handleLoadMore = () => {
        if (nextCursor && !loadingMore) {
            loadPayouts(nextCursor, true);
        }
    };

    const formatCurrency = (amount: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(amount);
    };

    const formatDateTime = (dateString: string | null) => {
        if (!dateString) return '';
        try {
            return format(new Date(dateString), 'MMM d, yyyy h:mm a');
        } catch {
            return dateString;
        }
    };

    const handleMipSync = async (payoutId: string) => {
        setSyncingPayoutId(payoutId);
        setSyncError(null);
        setSyncSuccess(null);
        
        try {
            const result = await triggerMipGiftSync(payoutId);
            
            // Update the payout in state with new MIP status
            setPayouts((prev) =>
                prev.map((p) =>
                    p.id === payoutId
                        ? {
                              ...p,
                              mip_sync_status: result.mip_sync_status as StripePayout['mip_sync_status'],
                              mip_synced_at: result.mip_synced_at,
                              mip_records_created: result.mip_records_created,
                              mip_error_message: result.mip_error_message,
                              mip_failed_documents: result.mip_failed_documents,
                          }
                        : p
                )
            );
            
            if (result.mip_sync_status === 'failed' || result.mip_failed_documents?.length) {
                setSyncError({
                    payoutId,
                    message: result.mip_error_message || 'MIP rejected some documents',
                });
                return;
            }

            setSyncSuccess({ payoutId, recordCount: result.mip_records_created });
            
            // Clear success message after 5 seconds
            setTimeout(() => setSyncSuccess(null), 5000);
        } catch (e) {
            setSyncError({
                payoutId,
                message: e instanceof Error ? e.message : 'Failed to sync to MIP',
            });
        } finally {
            setSyncingPayoutId(null);
        }
    };

    return (
        <>
            <Breadcrumb pageName='Stripe Payouts' />

            <div className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex flex-col gap-2'>
                    <p className='text-sm text-gray-500 dark:text-gray-400'>
                        {dataSource === 'stripe'
                            ? 'View and monitor Stripe payouts to your bank account.'
                            : 'View locally synced payouts including test payouts.'}
                    </p>
                    {totalCount !== null && (
                        <p className='text-xs text-gray-400 dark:text-gray-500'>
                            {totalCount} synced payout{totalCount !== 1 ? 's' : ''} found
                        </p>
                    )}
                </div>
                <div className='flex items-center gap-4'>
                    {/* Create Mock Payout button - only in Synced Records view */}
                    {dataSource === 'synced' && (
                        <button
                            onClick={() => setShowMockPayoutModal(true)}
                            className='inline-flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-opacity-90'
                        >
                            <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
                            </svg>
                            Mock Payout
                        </button>
                    )}
                    {/* Data Source Toggle */}
                    <div className='flex items-center rounded-lg border border-stroke dark:border-strokedark'>
                        <button
                            onClick={() => {
                                setDataSource('stripe');
                                setStatusFilter(''); // Reset filter when switching
                            }}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                                dataSource === 'stripe'
                                    ? 'bg-primary text-white'
                                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-meta-4'
                            } rounded-l-lg`}
                        >
                            Stripe API
                        </button>
                        <button
                            onClick={() => {
                                setDataSource('synced');
                                setStatusFilter(''); // Reset filter when switching
                            }}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                                dataSource === 'synced'
                                    ? 'bg-primary text-white'
                                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-meta-4'
                            } rounded-r-lg`}
                        >
                            Synced Records
                        </button>
                    </div>
                    {/* Status Filter */}
                    <div className='flex items-center gap-2'>
                        <label htmlFor='status-filter' className='text-sm font-medium'>
                            Status:
                        </label>
                        <select
                            id='status-filter'
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className='rounded border border-stroke bg-transparent px-3 py-1.5 text-sm outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark'
                        >
                            {dataSource === 'stripe' ? (
                                <>
                                    <option value=''>All</option>
                                    <option value='pending'>Pending</option>
                                    <option value='in_transit'>In Transit</option>
                                    <option value='paid'>Paid</option>
                                    <option value='failed'>Failed</option>
                                    <option value='canceled'>Canceled</option>
                                </>
                            ) : (
                                <>
                                    <option value=''>All</option>
                                    <option value='pending'>Pending</option>
                                    <option value='processing'>Processing</option>
                                    <option value='completed'>Completed</option>
                                    <option value='failed'>Failed</option>
                                </>
                            )}
                        </select>
                    </div>
                </div>
            </div>

            {error && (
                <div className='mb-6 rounded-sm border border-danger bg-danger bg-opacity-10 px-4 py-3 text-danger'>
                    {error}
                </div>
            )}

            {syncError && (
                <div className='mb-6 rounded-sm border border-danger bg-danger bg-opacity-10 px-4 py-3 text-danger'>
                    MIP Sync failed for {syncError.payoutId}: {syncError.message}
                </div>
            )}

            {syncSuccess && (
                <div className='mb-6 rounded-sm border border-success bg-success bg-opacity-10 px-4 py-3 text-success'>
                    Successfully synced {syncSuccess.recordCount} records to MIP for payout {syncSuccess.payoutId}
                </div>
            )}

            <section className='data-table-common rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark'>
                {loading ? (
                    <div className='flex items-center justify-center py-10'>
                        <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
                    </div>
                ) : payouts.length === 0 ? (
                    <div className='py-10 text-center text-gray-500'>
                        No payouts found.
                    </div>
                ) : (
                    <>
                        <div className='overflow-x-auto'>
                            <table className='datatable-table datatable-one w-full table-fixed border-collapse overflow-hidden break-words'>
                                <thead className='border-separate'>
                                    <tr
                                        className='border-t border-stroke bg-whiten dark:bg-meta-4'
                                        style={{ fontSize: '14px' }}
                                    >
                                        <th className='w-[18%] px-3 py-4 text-left font-medium'>
                                            Payout ID
                                        </th>
                                        <th className='w-[10%] px-3 py-4 text-right font-medium'>
                                            Amount
                                        </th>
                                        <th className='w-[12%] px-3 py-4 text-left font-medium'>
                                            Arrival Date
                                        </th>
                                        <th className='w-[14%] px-3 py-4 text-left font-medium'>
                                            Created
                                        </th>
                                        <th className='w-[8%] px-3 py-4 text-center font-medium'>
                                            Status
                                        </th>
                                        <th className='w-[10%] px-3 py-4 text-center font-medium'>
                                            Type
                                        </th>
                                        <th className='w-[12%] px-3 py-4 text-center font-medium'>
                                            MIP Sync
                                        </th>
                                        <th className='w-[16%] px-3 py-4 text-center font-medium'>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payouts.map((payout) => {
                                        const hasPersistedMipErrors = Boolean(
                                            payout.mip_error_message?.trim() || payout.mip_failed_documents?.length
                                        );
                                        const effectiveMipSyncStatus =
                                            payout.mip_sync_status === 'synced' && hasPersistedMipErrors
                                                ? 'failed'
                                                : payout.mip_sync_status;
                                        const canAttemptMipSync =
                                            payout.automatic &&
                                            payout.has_sync_record &&
                                            payout.mip_sync_status !== 'n/a' &&
                                            (payout.charges_processed === undefined || payout.charges_processed > 0);

                                        return (
                                        <tr
                                            key={payout.id}
                                            className='border-t border-stroke dark:border-strokedark'
                                        >
                                            <td className='px-3 py-4'>
                                                <div className='flex items-center gap-2'>
                                                    <Link
                                                        to={`/payouts/${payout.id}`}
                                                        className='inline-block truncate rounded bg-gray-100 px-2 py-1 text-xs hover:bg-primary hover:text-white dark:bg-meta-4 dark:hover:bg-primary transition-colors'
                                                        title={payout.id}
                                                    >
                                                        <code>{payout.id}</code>
                                                    </Link>
                                                    {'is_test_payout' in payout && payout.is_test_payout && (
                                                        <span className='inline-block flex-shrink-0 rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900 dark:text-orange-300'>
                                                            Test
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className='px-3 py-4 text-right font-medium'>
                                                {formatCurrency(payout.amount, payout.currency)}
                                            </td>
                                            <td className='px-3 py-4 whitespace-nowrap'>
                                                {formatCalendarDate(payout.arrival_date)}
                                            </td>
                                            <td className='px-3 py-4 whitespace-nowrap text-sm'>
                                                {formatDateTime(payout.created)}
                                            </td>
                                            <td className='px-3 py-4 text-center'>
                                                <span
                                                    className={`inline-block rounded-full px-3 py-1 text-xs font-medium capitalize ${
                                                        statusColors[payout.status] ||
                                                        'bg-gray-200 text-gray-700'
                                                    }`}
                                                >
                                                    {payout.status.replace('_', ' ')}
                                                </span>
                                            </td>
                                            <td className='px-3 py-4 text-center'>
                                                <span
                                                    className={`inline-block whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium ${
                                                        payout.automatic
                                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                                            : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                                    }`}
                                                >
                                                    {payout.automatic ? 'Scheduled' : 'Manual'}
                                                </span>
                                            </td>
                                            <td className='px-3 py-4 text-center'>
                                                <div className='flex flex-col items-center gap-1'>
                                                    <span
                                                        className={`inline-block whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium capitalize ${
                                                            mipStatusColors[effectiveMipSyncStatus] ||
                                                            'bg-gray-200 text-gray-600'
                                                        }`}
                                                    >
                                                        {effectiveMipSyncStatus === 'n/a'
                                                            ? 'N/A'
                                                            : effectiveMipSyncStatus === 'not_processed'
                                                            ? 'Not Processed'
                                                            : effectiveMipSyncStatus}
                                                    </span>
                                                    {payout.mip_sync_status === 'synced' &&
                                                        !hasPersistedMipErrors &&
                                                        payout.mip_records_created > 0 && (
                                                            <span className='text-xs text-gray-500'>
                                                                {payout.mip_records_created} records
                                                            </span>
                                                        )}
                                                </div>
                                            </td>
                                            <td className='px-3 py-4 text-center'>
                                                <div className='flex flex-wrap items-center justify-center gap-2'>
                                                    <Link
                                                        to={`/payouts/${payout.id}`}
                                                        className='inline-flex items-center justify-center rounded border border-stroke px-3 py-1 text-xs font-medium text-black transition-colors hover:border-primary hover:text-primary dark:border-strokedark dark:text-white dark:hover:border-primary dark:hover:text-primary'
                                                    >
                                                        View
                                                    </Link>
                                                    {/* Only show Sync to MIP when charges were actually processed */}
                                                    {canAttemptMipSync && (
                                                            <button
                                                                onClick={() => handleMipSync(payout.id)}
                                                                disabled={
                                                                    syncingPayoutId === payout.id ||
                                                                    payout.mip_sync_status === 'processing'
                                                                }
                                                                className='inline-flex items-center justify-center gap-1 rounded border border-primary px-3 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary hover:text-white disabled:opacity-50'
                                                            >
                                                                {syncingPayoutId === payout.id ? (
                                                                    <>
                                                                        <div className='h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent'></div>
                                                                        Syncing...
                                                                    </>
                                                                ) : (
                                                                    payout.mip_sync_status === 'synced' ? 'Re-sync to MIP' : 'Sync to MIP'
                                                                )}
                                                            </button>
                                                        )}
                                                    {payout.mip_sync_status === 'synced' && !hasPersistedMipErrors && (
                                                        <span className='text-xs text-gray-500'>
                                                            {payout.mip_synced_at
                                                                ? formatDateTime(payout.mip_synced_at)
                                                                : 'Synced'}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {hasMore && (
                            <div className='flex justify-center border-t border-stroke px-4 py-4 dark:border-strokedark'>
                                <button
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    className='inline-flex items-center justify-center gap-2 rounded-full border border-primary px-6 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white disabled:opacity-50'
                                >
                                    {loadingMore ? (
                                        <>
                                            <div className='h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent'></div>
                                            Loading...
                                        </>
                                    ) : (
                                        'Load More'
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </section>
            <CreateMockPayoutModal
                isOpen={showMockPayoutModal}
                onClose={() => setShowMockPayoutModal(false)}
                onCreated={() => {
                    // Reload synced payouts when a mock payout is created
                    if (dataSource === 'synced') {
                        loadPayouts();
                    }
                }}
            />
        </>
    );
};

export default Payouts;
